import * as React from 'react';
import { ScreenCapture } from './ScreenCapture';
import { AnnotationEditor } from './AnnotationEditor';
import type { CapturedImage, ScreenshotResult } from './types';

/**
 * Capture → annotate → result orchestrator (plan §5.5).
 *
 * Wires {@link ScreenCapture} to {@link AnnotationEditor}: a captured frame
 * opens the editor; saving hands the flattened `ScreenshotResult` to the
 * caller. Owns the decoded `ImageBitmap` lifecycle — it calls `.close()` on
 * both save and cancel so decoded-image memory is freed promptly (reviewer
 * note from WS2 review).
 */
export interface ScreenshotCaptureFlowProps {
  onComplete: (result: ScreenshotResult) => void;
  /** Disable capture (e.g. the staging cap was reached). */
  disabled?: boolean;
}

export function ScreenshotCaptureFlow({
  onComplete,
  disabled = false,
}: ScreenshotCaptureFlowProps): JSX.Element {
  const [captured, setCaptured] = React.useState<CapturedImage | null>(null);

  const closeBitmap = React.useCallback((img: CapturedImage | null) => {
    // ImageBitmap.close may be absent on very old engines that reached this
    // path via the file/paste fallback — guard it.
    img?.bitmap.close?.();
  }, []);

  const handleSave = React.useCallback(
    (result: ScreenshotResult) => {
      closeBitmap(captured);
      setCaptured(null);
      onComplete(result);
    },
    [captured, closeBitmap, onComplete],
  );

  const handleCancel = React.useCallback(() => {
    closeBitmap(captured);
    setCaptured(null);
  }, [captured, closeBitmap]);

  return (
    <>
      <ScreenCapture onCapture={setCaptured} disabled={disabled || captured !== null} />
      {captured && (
        <AnnotationEditor image={captured} onSave={handleSave} onCancel={handleCancel} />
      )}
    </>
  );
}
